import React, { useLayoutEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { CardContainer, Container } from "../../components/Container";
import { Byline, H6 } from "../../components/Typography";
import { withInstallation } from "../../components/InstallationContext";
import { MediaPlayer } from "dashjs";
import { getCameraStreams, getParklioBays } from "./queries";
import drivoApollo from "../../lib/drivoApollo";
import { Button } from "../../components/Button";
import { openGateMutation, restartQuestTerminal, staffBollardOverrideMutation } from "./mutations";
import { Loading } from "../../components/LoadingFork";
import { Well } from "../../components/Well";
import { RootOnly } from "../../components/AdminOnly";
import { throttle } from "lodash";
import { css } from "glamor";
import { blue, gray40, green, red } from "../../theme";
import { normalizeButtonKeys } from "./buzzoutButtons";
import { BollardOverride } from "./BollardOverride";

const statusStyles = {
  width: "10px",
  height: "10px",
  borderRadius: "50%",
  display: "inline-block",
};

const allBuzzoutButtons = {
  CONTRACTOR: "Contractor",
  FOC: "FOC",
  HOTEL_GUEST: "Guest No booking",
  AGGRESSIVE: "Aggressive",
  STAFF: "Staff No Booking",
  EMERGENCY: "Emergency",
  LATE_CHECK_OUT: "Late Check Out",
  OWNER: "Owner or Resident",
  GOLF_CLUB: "Golf Club",
  SCOOTER: "Scooter",
  PSG: "PSG",
  DAY_USE: "Day Use",
  MOTORBIKE: "Motorbike",
  GREEN_MOTION_CAR_HIRE: "Green Motion Car Hire",
  PLATE_READ_ISSUE: "Plate Read Issue",
};

const defaultBuzzoutButtons = [
  "CONTRACTOR",
  "FOC",
  "HOTEL_GUEST",
  "AGGRESSIVE",
  "STAFF",
  "EMERGENCY",
  "LATE_CHECK_OUT",
  "OWNER",
];

const getFeatureConfiguration = (configurations, featureName) => {
  return configurations.find(
    ({ feature }) => feature.name === featureName
  );
};

const GATE_OPERATION_THROTTLE_MS = 3_000;
const CLEAR_MESSAGE_INTERVAL_MS = 5_000;

const BOLLARD_OUTCOME_MESSAGE = {
  SUCCESS: (label, action) =>
    `Bay ${label} ${action === "OPEN" ? "opened" : "closed"}.`,
  BARRIER_BUSY: (label) => `Bay ${label}: barrier busy — try again.`,
  JAMMED: (label) => `Bay ${label}: bollard jammed — check the bay.`,
  CAR_PRESENT: (label) =>
    `Bay ${label}: a vehicle is on the bay — the bollard will not raise.`,
  BAY_NOT_FOUND: () => "Bay not found.",
  BAY_UNCONFIGURED: (label) => `Bay ${label} is not configured for Parklio.`,
};

function Streams({ installation, facility }) {
  const [terminalMessage, setTerminalMessage] = useState(null);
  const [gateMessage, setGateMessage] = useState(null);
  const { data = {}, loading } = useQuery(getCameraStreams, {
    fetchPolicy: "no-cache",
    variables: {
      id: installation.id,
    },
  });

  const [bollardBusy, setBollardBusy] = useState(false);
  const [bollardMessage, setBollardMessage] = useState(null);
  const {
    data: baysData,
    loading: baysLoading,
    refetch: refetchBays,
  } = useQuery(getParklioBays, {
    fetchPolicy: "no-cache",
    // Poll so a bay recovering from car-on-top / unreadable clears its disabled
    // or hidden controls without needing a command or a page reload (DRI-3297).
    pollInterval: 15000,
    variables: { installationId: installation.id },
  });
  const bays = baysData?.parklioBaysForInstallation ?? [];

  const buzzoutConfig = getFeatureConfiguration(
    facility?.featureFacilityProviderConfigurations || [],
    "Console Buzzout Buttons",
  );
  const activeButtonKeys =
    normalizeButtonKeys(buzzoutConfig?.configuration?.buttons) || defaultBuzzoutButtons;
  const activeBuzzoutButtons = Object.fromEntries(
    activeButtonKeys
      .filter((key) => allBuzzoutButtons[key])
      .map((key) => [key, allBuzzoutButtons[key]]),
  );

  const allStreams = data?.StreamUrl ?? [];
  const activeStreamsCount = allStreams.filter(
    (s) => s.url && !s.error,
  ).length;

  useLayoutEffect(() => {
    window.dash = {};
    return () => {
      console.log("unmounting", window.dash);
      for (let gateId of Object.keys(window.dash)) {
        let dashStream = window.dash[gateId];
        console.log("unmounted", gateId);
        dashStream.reset();
      }
    };
  }, []);

  const restartTerminal = async (gateId) => {
    try {
      console.log("Sending restart quest terminal request to ", gateId);
      await drivoApollo.mutate({
        mutation: restartQuestTerminal,
        variables: { gateId },
      });
      setTerminalMessage("Request was successful. Terminal will be restarted.");
    } catch (e) {
      setTerminalMessage(`Something went wrong. Error: ${e.message}`);
    }
    setTimeout(() => setTerminalMessage(null), CLEAR_MESSAGE_INTERVAL_MS);
  };

  function formatGateName(name) {
    if (!name) return "";

    const clean = name.trim().toLowerCase();

    // If gate contains a number -> extract and simplify
    const match = clean.match(/\b(entry|exit)[\s_-]*(\d+)\b/);
    if (match) {
      const direction = match[1][0].toUpperCase() + match[1].slice(1);
      const number = match[2];
      return `${direction} ${number}`;
    }

    // just capitalize each word
    return clean
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  const openGate = throttle(
    async (gateId, reason) => {
      console.log("Sending open gate request to", gateId, reason);
      try {
        await drivoApollo.mutate({
          mutation: openGateMutation,
          variables: { id: gateId, reason },
        });
        setGateMessage("Request was successful. Gate will be opened.");
      } catch (e) {
        setGateMessage(`Something went wrong. Error: ${e.message}`);
      } finally {
        setTimeout(() => setGateMessage(null), CLEAR_MESSAGE_INTERVAL_MS);
      }
    },
    GATE_OPERATION_THROTTLE_MS,
    { trailing: false },
  );

  const onBollardCommand = async (bayId, action, reason) => {
    setBollardBusy(true);
    setBollardMessage(null);
    try {
      const { data: result } = await drivoApollo.mutate({
        mutation: staffBollardOverrideMutation,
        variables: { bayId, action, reason },
      });
      const res = result?.staffBollardOverride;
      const build =
        BOLLARD_OUTCOME_MESSAGE[res?.outcome] || (() => "Command sent.");
      setBollardMessage(build(res?.bayLabel ?? "", action));
      await refetchBays();
    } catch (e) {
      setBollardMessage(`Something went wrong. Error: ${e.message}`);
    } finally {
      setBollardBusy(false);
      setTimeout(() => setBollardMessage(null), CLEAR_MESSAGE_INTERVAL_MS);
    }
  };

  return (
    <div>
      <CardContainer
        styles={{ backgroundColor: "rgb(241 245 249 / 0.8)", padding: 0 }}
      >
        <Container size="full" spaceBelow="medium">
          <h1 style={{ marginBottom: "0.5rem" }}>Camera Streams</h1>
          <Byline>Your facility's live cameras</Byline>
          {!loading && (
            <Byline
              style={{ display: "flex", alignItems: "center", gap: "1rem" }}
            >
              <span
                style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
              >
                <span
                  style={{
                    ...statusStyles,
                    backgroundColor: activeStreamsCount === 0 ? red : blue,
                  }}
                ></span>
                {activeStreamsCount} Cameras Live
              </span>
            </Byline>
          )}

          {terminalMessage && <H6>{terminalMessage}</H6>}
          {gateMessage && <H6>{gateMessage}</H6>}
        </Container>

        {bays.length > 0 && (
          <Container size="full" spaceBelow="medium">
            <BollardOverride
              bays={bays}
              loading={baysLoading}
              busy={bollardBusy}
              message={bollardMessage}
              onCommand={onBollardCommand}
            />
          </Container>
        )}

        {loading ? (
          <Loading />
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "1%",
            }}
          >
            {allStreams.map((u) => (
              <Well
                key={u.gateId}
                flavour={"white"}
                style={{
                  flex: "0 0 49%",
                  marginBottom: "24px",
                  marginTop: "0",
                  flexDirection: "column",
                  textAlign: "left",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "0.5rem",
                  }}
                >
                  <h3 style={{ width: "50%", marginTop: 0 }}>
                    {formatGateName(u.name)}
                  </h3>
                  {(u.gateType === "EXIT" && u.signId && facility.payment_method === 'QUEST') && (
                    <RootOnly>
                      <Button
                        size={"small"}
                        onClick={() => restartTerminal(u.gateId)}
                        className={css({
                          backgroundColor: "#f97316",
                          height: "40px",
                          borderRadius: "0.5rem",
                          marginTop: "-20px",
                          marginBottom: "0",
                          ":hover": {
                            backgroundColor: "#ea580c",
                          },
                        })}
                      >
                        Restart Quest Terminal
                      </Button>
                    </RootOnly>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    height: "300px",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "1rem",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      maxWidth: u.signId || u.terminalHidden ? "50%" : "100%",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    {u.url ? (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          maxWidth: u.signId || u.terminalHidden ? "100%" : "",
                          borderRadius: "8px",
                          overflow: "hidden",
                          position: "relative",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <video
                          style={{
                            width: "100%",
                            objectFit: "contain",
                            display: "block",
                            borderRadius: "1rem",
                          }}
                          ref={(ref) => {
                            if (!ref) return;

                            // "Loading stream..." placeholder text while loading
                            const loadingText = document.createElement("div");
                            loadingText.textContent = "Loading stream...";
                            loadingText.style.position = "absolute";
                            loadingText.style.color = "#888";
                            loadingText.style.fontSize = "0.9rem";
                            ref.parentElement?.insertBefore(loadingText, ref);

                            // initialize Dash.js
                            const dashjs = MediaPlayer().create();
                            window.dash[u.gateId] = dashjs;
                            dashjs.initialize(ref, u.url, true);

                            // remove loading text once video starts playing or errors out
                            const removeText = () => loadingText.remove();
                            ref.addEventListener("playing", removeText, {
                              once: true,
                            });
                            ref.addEventListener("error", removeText, {
                              once: true,
                            });
                          }}
                          autoPlay
                          muted
                          playsInline
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "8px",
                          backgroundColor: "#e2e8f0",
                          color: "#222",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          fontSize: "0.9rem",
                          fontStyle: "italic",
                          maxWidth: u.signId || u.terminalHidden ? "100%" : "",
                          overflow: "hidden",
                        }}
                      >
                        No streaming configured
                      </div>
                    )}
                  </div>
                  {u.gateType === "EXIT" && (u.signId || u.terminalHidden) && (
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        maxWidth: "50%",
                        justifyContent: "stretch",
                        height: "100%",
                      }}
                    >
                      {u.signId ? (
                        <iframe
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            width: "auto",
                            height: "auto",
                            borderRadius: "8px",
                          }}
                          title={`Signs-${u.signId}`}
                          src={`https://drivo.io/signs/${u.signId}`}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "8px",
                            backgroundColor: "#e2e8f0",
                            color: "#222",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontSize: "0.9rem",
                            fontStyle: "italic",
                            overflow: "hidden",
                          }}
                        >
                          No terminal configured
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <h4 style={{ width: "100%", color: gray40 }}>
                  Open Gate - Select Reason
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "0.5rem",
                    marginTop: "10px",
                  }}
                >
                  {Object.entries(activeBuzzoutButtons).map(([key, label]) => (
                    <Button
                      key={key}
                      size="small"
                      onClick={() => openGate(u.gateId, key)}
                      className={css({
                        width: "100%",
                        height: "45px",
                        textAlign: "center",
                        justifyContent: "center",
                        color: "#4338ca",
                        backgroundColor: "#eef2ff",
                        transition: "background-color 0.15s ease",
                        ":hover": {
                          backgroundColor: "#e0e7ff",
                        },
                      })}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </Well>
            ))}
          </div>
        )}
      </CardContainer>
    </div>
  );
}

export default withInstallation(Streams);
